import React from 'react';
import { FaClock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

// Helper to format assigned room numbers into ranges
const formatRoomNumbers = (roomNumbers) => {
  if (!roomNumbers || roomNumbers.length === 0) return 'Sin asignar';

  const sortedNumbers = [...roomNumbers].sort((a, b) => a - b);
  const ranges = [];
  let start = sortedNumbers[0];
  let end = sortedNumbers[0];

  for (let i = 1; i < sortedNumbers.length; i++) {
    if (sortedNumbers[i] === end + 1) {
      end = sortedNumbers[i];
    } else {
      if (start === end) {
        ranges.push(start.toString());
      } else {
        ranges.push(`${start}-${end}`);
      }
      start = sortedNumbers[i];
      end = sortedNumbers[i];
    }
  }

  if (start === end) {
    ranges.push(start.toString());
  } else {
    ranges.push(`${start}-${end}`);
  }

  return ranges.join(' - ');
};

const ReservationSummary = React.forwardRef(({ reservation }, ref) => {
  if (!reservation) return null;

  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'N/A';

  const getStatusInfo = (status) => {
    switch (status) {
      case 'confirmed':
        return { text: 'Confirmada', icon: <FaCheckCircle />, color: 'text-green-600' };
      case 'cancelled':
        return { text: 'Cancelada', icon: <FaTimesCircle />, color: 'text-red-600' };
      case 'pending':
      default:
        return { text: 'Pendiente', icon: <FaClock />, color: 'text-yellow-600' };
    }
  };
  
  const statusInfo = getStatusInfo(reservation.status);

  const roomDetails = () => {
    if (reservation.room_types && Array.isArray(reservation.room_types) && reservation.room_types.length > 0) {
      return reservation.room_types.map((room, idx) => (
        <p key={idx} className="ml-4 text-sm">• {room.type}: {room.quantity}</p>
      ));
    }
    if (reservation.room_type) {
      return <p className="ml-4 text-sm">• {reservation.room_type}: {reservation.number_of_rooms}</p>;
    }
    return <p className="ml-4 text-sm">No hay detalles de habitación.</p>;
  };

  return (
    <div ref={ref} className="reservation-summary p-4 sm:p-6 md:p-8 bg-white max-w-full" style={{ fontFamily: 'sans-serif' }}>
      <div className="text-center mb-4 sm:mb-6">
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-playfair font-bold text-gray-900">Comprobante de Reserva</h3>
        <p className="text-base sm:text-lg text-gray-600">Sumaq Mayu - Casa Hotel</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6 border-y py-3 sm:py-4 border-gray-200">
        <div>
          <p className="text-base sm:text-lg md:text-xl"><strong>ID de Reserva:</strong> <span className="font-semibold text-green-700">{reservation.id}</span></p>
        </div>
        <div className={`text-base sm:text-lg md:text-xl font-bold flex items-center gap-2 ${statusInfo.color}`}>
          {statusInfo.icon}
          <span>{statusInfo.text}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 text-gray-800 text-sm sm:text-base">
        <div className="space-y-2">
          <p className="break-words"><strong>Nombre Completo:</strong> {reservation.full_name}</p>
          <p><strong>DNI:</strong> {reservation.dni}</p>
          <p><strong>Celular:</strong> {reservation.phone}</p>
          <p className="break-words"><strong>Email:</strong> {reservation.email}</p>
          <p className="break-words"><strong>Ciudad de Procedencia:</strong> {reservation.city_of_origin}</p>
        </div>
        <div className="space-y-2">
          <p><strong>Fecha de Ingreso:</strong> {formatDate(reservation.checkin_date)}</p>
          <p><strong>Fecha de Salida:</strong> {formatDate(reservation.checkout_date)}</p>
          <p><strong>Nº de Huéspedes:</strong> {reservation.number_of_guests}</p>
          <p><strong>Nº de Niños:</strong> {reservation.number_of_children}</p>
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6 border-t pt-3 sm:pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 text-gray-800 text-sm sm:text-base">
          <div className="space-y-2">
            <p><strong>Habitaciones Solicitadas:</strong></p>
            <div className="pl-4">{roomDetails()}</div>
          </div>
          <div className="space-y-2">
            <p><strong>Nº de Habitaciones Asignadas:</strong></p>
            <p className="pl-4 font-bold text-base sm:text-lg text-blue-600">
              {formatRoomNumbers(reservation.assigned_room_number)}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-6 bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
        <h4 className="font-semibold text-base sm:text-lg mb-3 text-gray-800">Detalles del Pago</h4>
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Costo Total</p>
            <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900">S/. {parseFloat(reservation.total_cost || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Monto Pagado</p>
            <p className="text-base sm:text-lg md:text-xl font-bold text-green-600">S/. {parseFloat(reservation.amount_paid || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Monto Adeudado</p>
            <p className="text-base sm:text-lg md:text-xl font-bold text-red-600">S/. {parseFloat(reservation.amount_due || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 text-center text-xs sm:text-sm text-gray-500 border-t pt-3 sm:pt-4 border-gray-200">
        <p>Este es un documento generado automáticamente.</p>
        <p>Sumaq Mayu | Tarapoto, Perú</p>
      </div>
    </div>
  );
});

export default ReservationSummary;
